import type { ComponentChildren } from "preact";
import { useEffect, useLayoutEffect, useRef, useState } from "preact/hooks";
import { defineMessages, useT } from "../i18n/index.ts";
import { Icon } from "../icons/Icon.tsx";

const messages = defineMessages({ close: "Fermer" }, { close: "Close" });

interface Props {
  title: string;
  /** Bouton d'où descend la fenêtre (écrans larges). Sans ancre, ou sur mobile : feuille du bas. */
  anchor?: HTMLElement | null;
  wide: boolean;
  onClose(): void;
  actions?: ComponentChildren;
  /** Sur mobile, la feuille monte jusqu'en haut de l'écran (recherche : le clavier ne cache pas les résultats). */
  tall?: boolean;
  children: ComponentChildren;
}

const GAP = 8;
const MARGIN = 16;

/**
 * Fenêtre des réglages, de la table des matières et des signets : elle descend du bouton
 * cliqué sur grand écran et remonte du bas de l'écran sur mobile, comme les feuilles de l'app.
 */
export function Popover({ title, anchor, wide, onClose, actions, tall = false, children }: Props) {
  const t = useT(messages);
  const panel = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; right: number; originX: number } | null>(null);
  const dropdown = wide && !!anchor;
  const kind = dropdown ? "dropdown" : !wide && tall ? "tall" : "sheet";

  useLayoutEffect(() => {
    if (!dropdown || !anchor) return;
    const place = () => {
      const r = anchor.getBoundingClientRect();
      const width = panel.current?.offsetWidth ?? 380;
      const right = Math.max(MARGIN, window.innerWidth - r.right);
      // Point d'origine de l'animation : le centre du bouton, vu depuis le bord droit de la fenêtre.
      const originX = Math.min(width, Math.max(0, width - (window.innerWidth - right - (r.left + r.width / 2))));
      setPosition({ top: r.bottom + GAP, right, originX });
    };
    place();
    window.addEventListener("resize", place);
    return () => window.removeEventListener("resize", place);
  }, [dropdown, anchor]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    // Un champ du contenu (recherche) a déjà pris le focus : on le lui laisse.
    if (!panel.current?.contains(document.activeElement)) panel.current?.focus();
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const style = dropdown && position
    ? { top: `${position.top}px`, right: `${position.right}px`, transformOrigin: `${position.originX}px 0` }
    : undefined;

  return (
    <div class={`msb-popover-layer msb-popover-layer--${kind === "dropdown" ? "dropdown" : "sheet"}`}>
      <div class="msb-popover-scrim" onClick={onClose} />
      <div
        ref={panel}
        class={`msb-popover msb-popover--${kind}`}
        style={style}
        role="dialog"
        aria-label={title}
        tabIndex={-1}
      >
        {kind === "sheet" && <div class="msb-sheet-handle" aria-hidden="true" />}
        <header class="msb-popover__header">
          <button type="button" class="msb-icon-button" onClick={onClose} aria-label={t("close")}>
            <Icon name="close" />
          </button>
          <h2>{title}</h2>
          <div class="msb-popover__actions">{actions}</div>
        </header>
        <div class="msb-popover__body">{children}</div>
      </div>
    </div>
  );
}
